import React from 'react'
import {
    Box,
    CircularProgress,
    InputBase,
    MenuItem,
    Paper,
    Select,
    Tooltip,
    Typography,
} from '@mui/material'
import {
    Close as CloseIcon,
    InfoOutlined,
    Search as SearchIcon,
} from '@mui/icons-material'

export type CountOption = number | 'all'
export const COUNT_OPTIONS: CountOption[] = [10, 20, 'all']

interface Props {
    title?: string
    info?: string
    tabs?: React.ReactNode
    search: string
    onSearch: (v: string) => void
    searchPlaceholder?: string
    searching?: boolean
    count: CountOption
    onCount: (c: CountOption) => void
    children: React.ReactNode
}

/**
 * Shell for the Fleet "Top Performers" card. Owns the universal header controls
 * so they apply across every tab: the title, a search box, and a row-count
 * selector (Top 10 / 20 / All) — followed by the tab strip and the active grid.
 * The grids render in "embedded" mode (no chrome of their own).
 */
export const TopPerformersCard: React.FC<Props> = ({
    title = 'Top Performers',
    info,
    tabs,
    search,
    onSearch,
    searchPlaceholder = 'Search…',
    searching,
    count,
    onCount,
    children,
}) => {
    const controlH = 34
    return (
        <Paper
            elevation={0}
            sx={{
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{
                    px: 2.5,
                    pt: 2,
                    pb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                {info && (
                    <Tooltip title={info} arrow placement="top">
                        <InfoOutlined
                            sx={{
                                fontSize: 15,
                                color: 'text.disabled',
                                cursor: 'help',
                                '&:hover': { color: 'text.secondary' },
                            }}
                        />
                    </Tooltip>
                )}
                <Box sx={{ flexGrow: 1 }} />

                {/* Universal search — filters whichever grid is active. */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        height: controlH,
                        px: 1.25,
                        minWidth: 0,
                        width: { xs: 150, sm: 230 },
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.04)',
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'border-color 120ms, box-shadow 120ms',
                        '&:focus-within': {
                            borderColor: 'primary.main',
                            boxShadow: '0 0 0 3px rgba(139,196,65,0.18)',
                        },
                    }}
                >
                    <SearchIcon
                        sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }}
                    />
                    <InputBase
                        value={search}
                        onChange={(e) => onSearch(e.target.value)}
                        placeholder={searchPlaceholder}
                        sx={{
                            flex: 1,
                            fontSize: 13,
                            color: 'text.primary',
                            '& input::placeholder': {
                                color: 'text.disabled',
                                opacity: 1,
                            },
                        }}
                    />
                    {searching ? (
                        <CircularProgress size={13} sx={{ flexShrink: 0 }} />
                    ) : (
                        search && (
                            <CloseIcon
                                onClick={() => onSearch('')}
                                sx={{
                                    fontSize: 16,
                                    color: 'text.disabled',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    '&:hover': { color: 'text.secondary' },
                                }}
                            />
                        )
                    )}
                </Box>

                {/* Row-count selector. */}
                <Select
                    value={count}
                    onChange={(e) => onCount(e.target.value as CountOption)}
                    size="small"
                    sx={{
                        height: controlH,
                        fontSize: 13,
                        fontWeight: 600,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.04)',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'divider',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.disabled',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                            borderWidth: 1,
                        },
                        '& .MuiSelect-select': { py: 0, pr: '28px !important' },
                    }}
                    MenuProps={{
                        PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: 13 } } },
                    }}
                >
                    {COUNT_OPTIONS.map((o) => (
                        <MenuItem key={String(o)} value={o}>
                            {o === 'all' ? 'All' : `Top ${o}`}
                        </MenuItem>
                    ))}
                </Select>
            </Box>
            {tabs}
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{children}</Box>
        </Paper>
    )
}

export default TopPerformersCard
